import { WIZARD_BUILDER_STEPS } from '../../constants.js';
import type { LocationGrounding, WizardStepId, WorldDraft } from '../../domain/types.js';
import { firstIncompleteScheduleField, validateScheduleStep } from './hoursStep.js';
import { firstIncompletePeriodField, trimTimePeriod, validateTimePeriod } from './periodStep.js';
import {
  DEFAULT_GROUNDING_LANGUAGE,
  firstIncompleteConnectedField,
  firstIncompleteGroundingField,
  trimGrounding,
  validateConnected,
  validateGrounding,
} from './questionSchema.js';

/** Legacy CreateWorld routes still used until B4 renames character screens. */
export type WizardRouteStep =
  | WizardStepId
  | 'conditional-questions'
  | 'character-population'
  | 'world-generation'
  | 'mode'
  | 'location-type';

/** Active wizard route — mode and location-type screens removed (notes §5). */
export const WIZARD_STEP_ORDER: WizardRouteStep[] = [
  'welcome',
  'location-grounding',
  'connected',
  'time-period',
  'operating-hours',
  'character-population',
  'review',
  'world-generation',
  'ready-to-launch',
];

const REMOVED_STEPS = new Set<WizardRouteStep>(['mode', 'location-type']);

/** Map legacy saved-draft steps to canonical v2 screens (never render deleted screens). */
export function normalizeWizardStep(step: WizardRouteStep): WizardRouteStep {
  if (REMOVED_STEPS.has(step)) return 'location-grounding';
  if (step === 'conditional-questions') return 'connected';
  if (step === 'saved-draft') return 'welcome';
  return step;
}

export function stepIndex(step: WizardRouteStep): number {
  const normalized = normalizeWizardStep(step);
  const i = WIZARD_STEP_ORDER.indexOf(normalized);
  return i >= 0 ? i : 0;
}

export function nextStep(step: WizardRouteStep, skipWelcome = false): WizardRouteStep {
  const order = orderedSteps(skipWelcome);
  const normalized = normalizeWizardStep(step);
  const i = order.indexOf(normalized);
  if (i < 0) return step;
  return order[Math.min(i + 1, order.length - 1)] ?? step;
}

export function prevStep(step: WizardRouteStep, skipWelcome = false): WizardRouteStep {
  const order = orderedSteps(skipWelcome);
  const normalized = normalizeWizardStep(step);
  const i = order.indexOf(normalized);
  if (i < 0) return step;
  return order[Math.max(i - 1, 0)] ?? step;
}

export function progressPercent(step: WizardRouteStep): number {
  const i = stepIndex(step);
  return Math.round((i / (WIZARD_STEP_ORDER.length - 1)) * 100);
}

export function builderStepTotal(skipWelcome: boolean): number {
  return skipWelcome ? WIZARD_BUILDER_STEPS.length : WIZARD_BUILDER_STEPS.length + 1;
}

export function builderStepNumber(step: WizardRouteStep, skipWelcome: boolean): number {
  const normalized = normalizeWizardStep(step);
  if (normalized === 'welcome') return skipWelcome ? 0 : 1;
  const idx = WIZARD_BUILDER_STEPS.indexOf(normalized as (typeof WIZARD_BUILDER_STEPS)[number]);
  if (idx >= 0) return skipWelcome ? idx + 1 : idx + 2;
  const total = builderStepTotal(skipWelcome);
  if (['review', 'character-population', 'characters-basic', 'characters-detailed', 'characters-traits'].includes(normalized)) {
    return total;
  }
  return total;
}

export function continueLabel(step: WizardRouteStep, skipWelcome: boolean): string {
  const next = nextStep(step, skipWelcome);
  const target = builderStepNumber(next, skipWelcome);
  const total = builderStepTotal(skipWelcome);
  if (step === 'welcome') return 'Start building';
  if (next === 'world-generation' || next === 'review') return 'Continue';
  if (target <= 0 || target > total) return 'Continue';
  return `Continue to step ${target} of ${total}`;
}

export function orderedSteps(skipWelcome: boolean): WizardRouteStep[] {
  return skipWelcome ? WIZARD_STEP_ORDER.filter((s) => s !== 'welcome') : WIZARD_STEP_ORDER;
}

export function resetToastMessage(alreadyResetInSession: boolean): string {
  return alreadyResetInSession
    ? 'Clicked reset previously, nothing is saved'
    : 'Draft reset.';
}

export function hasSaveWorldProgress(draft: WorldDraft): boolean {
  const loc = draft.location;
  const lang = loc?.language?.trim();
  if (loc?.name?.trim() || loc?.region?.trim()) return true;
  if (lang && lang !== DEFAULT_GROUNDING_LANGUAGE) return true;
  if (draft.connected) return true;
  if (draft.timePeriod) return true;
  if (draft.schedule) return true;
  return Object.keys(draft.answers).length > 0 || draft.characterDrafts.length > 0;
}

export function hasResetProgress(draft: WorldDraft): boolean {
  return Boolean(draft.location?.name?.trim());
}

/** @deprecated use validateGrounding from questionSchema */
export function validateLocationGrounding(
  _type: unknown,
  location: LocationGrounding | null,
): string | null {
  return validateGrounding(location);
}

export function firstIncompleteFieldForStep(
  draft: WorldDraft,
  step: WizardRouteStep,
): string | null {
  switch (normalizeWizardStep(step)) {
    case 'location-grounding':
      return firstIncompleteGroundingField(draft.location);
    case 'connected':
      return firstIncompleteConnectedField(draft.connected);
    case 'time-period':
      return firstIncompletePeriodField(draft.timePeriod);
    case 'operating-hours':
      return firstIncompleteScheduleField(draft.schedule, draft.location?.region);
    default:
      return null;
  }
}

export function canAdvanceFromStep(draft: WorldDraft, step: WizardRouteStep): string | null {
  switch (normalizeWizardStep(step)) {
    case 'welcome':
      return null;
    case 'location-grounding':
      return validateGrounding(draft.location);
    case 'connected':
      return validateConnected(draft.connected);
    case 'time-period':
      return validateTimePeriod(draft.timePeriod);
    case 'operating-hours':
      return validateScheduleStep(draft.schedule, draft.location?.region);
    case 'review':
      return validateConnected(draft.connected);
    case 'character-population': {
      const active = draft.characterDrafts.filter((c) => c.status !== 'removed');
      if (active.length < 1) return 'Add at least one character.';
      if (active.length > 10) return 'Maximum ten characters.';
      return null;
    }
    default:
      return null;
  }
}

/** Trim period fields before persisting step advances. */
export function normalizedTimePeriodForAdvance(draft: WorldDraft): WorldDraft {
  if (!draft.timePeriod) return draft;
  return { ...draft, timePeriod: trimTimePeriod(draft.timePeriod) };
}

/** Trim grounding fields before persisting step advances (not on every keystroke). */
export function normalizedLocationGroundingForAdvance(draft: WorldDraft): WorldDraft {
  if (!draft.location) return draft;
  return { ...draft, location: trimGrounding(draft.location) };
}

const NON_RESUME_STEPS = new Set<WizardStepId>(['welcome', 'saved-draft', 'launched']);

/** Builder + post-builder screens checked in order for legacy drafts without lastVisitedStep. */
const INCOMPLETE_STEP_CANDIDATES: WizardRouteStep[] = [
  'location-grounding',
  'connected',
  'time-period',
  'operating-hours',
  'character-population',
  'review',
];

/** First step with blocking validation (legacy drafts without a recorded visit step). */
export function inferFirstIncompleteStep(draft: WorldDraft): WizardStepId {
  for (const step of INCOMPLETE_STEP_CANDIDATES) {
    if (step === 'character-population') {
      if (canAdvanceFromStep(draft, step)) return 'character-population';
      continue;
    }
    if (step === 'review') {
      if (!draft.reviewAccepted) return 'review';
      continue;
    }
    if (canAdvanceFromStep(draft, step)) {
      return normalizeWizardStep(step) as WizardStepId;
    }
  }
  return 'review';
}

/** Resume target: last saved visit, else wizardStep, else first incomplete (legacy). */
export function resolveResumeStep(draft: WorldDraft, skipWelcome = false): WizardStepId {
  let resolved: WizardStepId;
  if (draft.lastVisitedStep && !NON_RESUME_STEPS.has(draft.lastVisitedStep)) {
    resolved = normalizeWizardStep(draft.lastVisitedStep as WizardRouteStep) as WizardStepId;
  } else if (!NON_RESUME_STEPS.has(draft.wizardStep)) {
    resolved = normalizeWizardStep(draft.wizardStep as WizardRouteStep) as WizardStepId;
  } else if (draft.wizardStep === 'saved-draft' || hasSaveWorldProgress(draft)) {
    resolved = inferFirstIncompleteStep(draft);
  } else {
    resolved = 'welcome';
  }
  if (skipWelcome && resolved === 'welcome') return 'location-grounding';
  return resolved;
}

/** Record visit step on Save World — preserves prior visit when still on welcome/saved-draft. */
export function visitStepForSave(
  draft: WorldDraft,
  activeStep: WizardRouteStep,
): WizardStepId | undefined {
  const normalized = normalizeWizardStep(activeStep) as WizardStepId;
  if (!NON_RESUME_STEPS.has(normalized)) return normalized;
  return draft.lastVisitedStep;
}

export function stepLabel(step: WizardRouteStep): string {
  const normalized = normalizeWizardStep(step);
  const labels: Partial<Record<WizardRouteStep, string>> = {
    welcome: 'Welcome',
    'location-grounding': 'Location details',
    connected: 'Connected',
    'time-period': 'Time period',
    'operating-hours': 'Operating hours',
    review: 'Review',
    'character-population': 'Characters',
    'world-generation': 'Generation',
    'ready-to-launch': 'Launch',
  };
  return labels[normalized] ?? normalized;
}

export function stepTitle(step: WizardRouteStep): string {
  const normalized = normalizeWizardStep(step);
  const titles: Partial<Record<WizardRouteStep, string>> = {
    welcome: 'Create your world',
    'location-grounding': 'Name and Location',
    connected: 'Connected',
    'time-period': 'Time period',
    'operating-hours': 'Operating/Working hours',
    review: 'Review and assumptions',
    'character-population': 'Populate your world',
    'world-generation': 'Generating your world',
    'ready-to-launch': 'Launching…',
  };
  return titles[normalized] ?? 'Create World';
}
